'use client';

import { useEffect, useState } from 'react';

import type { NavItem } from '../../types/navigation';

const getDefaultMegaGroupId = (item: NavItem): string | null => {
  if (!item.megaMenu) {
    return null;
  }

  return item.megaMenu.defaultGroupId || item.megaMenu.groups[0]?.id || null;
};

interface UseHeaderMobileAccordionParams {
  isOpen: boolean;
  navItems: NavItem[];
}

const useHeaderMobileAccordion = ({
  isOpen,
  navItems,
}: UseHeaderMobileAccordionParams) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [expandedMegaGroups, setExpandedMegaGroups] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!isOpen) {
      setExpandedItems([]);
      setExpandedMegaGroups({});
      return;
    }

    const nextDefaults: Record<string, string[]> = {};
    navItems.forEach((item) => {
      const defaultId = getDefaultMegaGroupId(item);

      if (defaultId) {
        nextDefaults[item.label] = [defaultId];
      }
    });

    setExpandedMegaGroups(nextDefaults);
  }, [isOpen, navItems]);

  const toggleAccordion = (itemLabel: string) => {
    setExpandedItems((previous) =>
      previous.includes(itemLabel)
        ? previous.filter((label) => label !== itemLabel)
        : [...previous, itemLabel],
    );
  };

  const toggleMegaGroup = (itemLabel: string, groupId: string) => {
    setExpandedMegaGroups((previous) => {
      const currentIds = previous[itemLabel] || [];
      const nextIds = currentIds.includes(groupId) ? [] : [groupId];

      return { ...previous, [itemLabel]: nextIds };
    });
  };

  return {
    expandedItems,
    expandedMegaGroups,
    toggleAccordion,
    toggleMegaGroup,
  };
};

export default useHeaderMobileAccordion;
